import { useEffect, useState } from 'react';
import type { VoidFunctionComponent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FeatureFlagRequired, QualityRequired } from '../../generic/Gates';
import FallbackImage from '../../generic/FallbackImage';
import BlockPackageModal from './BlockPackageModal';
import PackageDetailsMetadata from './PackageDetailsMetadata';
import PackageDetailsTable from './PackageDetailsTable';
import * as PackageInventoryStore from '../../../redux/packageInventory';
import type { PackageInventoryParams } from '../PackageInventoryContainer';
import { useParams } from '../../../contexts/RouterContext';
import ModalManagePackageLabels from '../../modals/ModalManagePackageLabels';
import { PACKAGE_LABELS } from '../../../../shared/featureFlags/flagNames';
import { useManagePackageLabelsModal } from '../../projects/dependenciesV2/hooks/useManagePackageLabelsModal';

/**
 * Hook which will fetch the package details whenever the packageLocator changes.
 *
 * @param {string} [packageLocator] - A given package locator.
 */
const usePackageData = (packageLocator?: string): void => {
  const dispatch = useDispatch();
  const hydrateStateQuery = useSelector(PackageInventoryStore.selectors.getHydratePackagesStateQuery);

  useEffect(() => {
    if (packageLocator) {
      dispatch(PackageInventoryStore.actions.fetchPackageDetails(packageLocator));
      dispatch(PackageInventoryStore.actions.fetchPackageMetadata(packageLocator));
    }
  }, [packageLocator, dispatch, hydrateStateQuery]);
};

/**
 * Package Details is rendered inside the package inventory page
 * when there is an active packageLocator. If there isn't one,
 * the parent component (PackageInventoryContainer) is responsible
 * for setting it.
 */
const PackageDetails: VoidFunctionComponent = () => {
  const params = useParams<PackageInventoryParams>();
  const managePackageLabelsModal = useManagePackageLabelsModal();

  usePackageData(params.packageLocator);

  const [isBlockModalDisplayed, setIsBlockModalDisplayed] = useState(false);
  const [isBlockingAllVersions, setIsBlockingAllVersions] = useState(false);
  const isPackageDetailsPageLoading = useSelector(PackageInventoryStore.selectors.getPackageDetailsPageLoading);
  const hasPackageErrors = useSelector(PackageInventoryStore.selectors.getHasPackageErrors);
  const hasNoPackages = useSelector(PackageInventoryStore.selectors.getHasNoPackages);
  const packageMetadata = useSelector(PackageInventoryStore.selectors.getPackageMetadata);

  if (hasPackageErrors) {
    return (
      <FallbackImage src="error-plug" alt="Network error" hasPadding>
        <div>Oops! There was an error.</div>
        <div>Please try again in a bit.</div>
      </FallbackImage>
    );
  }

  if (!isPackageDetailsPageLoading && hasNoPackages) {
    return (
      <FallbackImage src="no-results" alt="No packages found" hasPadding>
        No packages found.
      </FallbackImage>
    );
  }

  const onShowBlockModal = () => setIsBlockModalDisplayed(true);

  //  This function is specific to displaying the "Block Packages" button in the metadata section,
  //  since that will cause ALL future versions of the packaged to be denied.
  //  We do this by toggling the `isBlockingAllVersions`, which tells the BlockPackageModal
  //  to send 'ALL' instead of the selectedVersions list.
  const onShowBlockAllModal = () => {
    setIsBlockingAllVersions(true);
    onShowBlockModal();
  };

  const onHideBlockModal = () => {
    if (isBlockingAllVersions) {
      setIsBlockingAllVersions(false);
    }
    setIsBlockModalDisplayed(false);
  };

  const onShowManageLabelsModal = () => {
    managePackageLabelsModal.onOpen({
      context: { revision: params.packageLocator, title: packageMetadata?.name },
    });
  };

  return (
    <div className="package-inventory__details">
      <PackageDetailsMetadata
        onShowBlockAllModal={onShowBlockAllModal}
        onShowManageLabelsModal={onShowManageLabelsModal}
      />
      <PackageDetailsTable packageLocator={params.packageLocator} onShowBlockModal={onShowBlockModal} />
      <QualityRequired>
        <BlockPackageModal
          isDisplayed={isBlockModalDisplayed}
          onHide={onHideBlockModal}
          isBlockingAllVersions={isBlockingAllVersions}
          packageLocator={params.packageLocator as string}
        />
      </QualityRequired>
      <FeatureFlagRequired flagName={PACKAGE_LABELS}>
        <ModalManagePackageLabels
          show={managePackageLabelsModal.isOpen}
          onHide={managePackageLabelsModal.onClose}
          context={managePackageLabelsModal.context}
          isGlobalEnabled
          isProjectEnabled={false}
          isRevisionEnabled={false}
        />
      </FeatureFlagRequired>
    </div>
  );
};

export default PackageDetails;
